import { resetFindVehicle } from "Store/findVehicle/slice";
import { buttonNextTrackId, buttonTrackId } from "Utils/analytics";
import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CarProductIntroFormValues } from "../interfaces";

import { MediumText } from "App/global-style";
import { Select } from "Components/Form/Select";
import { useTexts } from "Components/TextContext";
import { Button as CommonButtonText } from "Components/TextContext/commonComponentEnums";
import { ThemedButton } from "Components/Themes/components/ThemedButton";
import { VEHICLE_TYPE } from "Features/CheckPrice/Products/RiskProducts/VehicleProducts/vehicleConstants";
import { useVehicleModelsByYearAndBrand } from "Store/findVehicleType/hooks";
import { ReduxState } from "Store/rootReducer";
import { isValidEntity } from "Utils/apiUtils";
import { isDefined } from "Utils/functions";
import { getError } from "Utils/productUtils";
import { FormikProps, useFormikContext } from "formik";
import {
  findVehicleTextsCar,
  handleVehicleModelTypeChange,
  modelTypeOptions,
} from "../FindVehicleType/utils";
import { VehicleContainer } from "../styled";
import { CarDescription } from "./styled";

export const FoundCarElement = (): React.ReactElement | null => {
  const { current: vehicle } = useSelector(
    (state: ReduxState) => state.findVehicle,
  );

  // sets focus on search result if there are any
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const inputElement = containerRef.current?.querySelector("input");
    if (inputElement) {
      inputElement.focus();
    }
  }, [vehicle, containerRef]);

  const formProps = useFormikContext<CarProductIntroFormValues>();
  const { getText } = useTexts();
  const dispatch = useDispatch();

  const models = useVehicleModelsByYearAndBrand(
    VEHICLE_TYPE.CAR,
    formProps.values.year,
    formProps.values.brand,
  );
  const modelsFetched = isValidEntity(models);

  if (!isDefined(vehicle)) {
    return null;
  }

  const foundVehicleWithMissingConfigId = !isDefined(vehicle.configurationId);

  const resetSearchTrackerId = buttonTrackId("search-vehicle-reset");

  return (
    <>
      <VehicleContainer ref={containerRef} variant="border" tabIndex={-1}>
        <CarDescription>
          <MediumText>
            {vehicle.brand} {vehicle.model} {vehicle.registrationYear}
          </MediumText>
          {vehicle.regNumber}
        </CarDescription>
        <ThemedButton
          id={buttonNextTrackId("check-price")}
          type="submit"
          onClick={() => formProps.submitForm()}
          trackId={buttonNextTrackId("check-price")}
        >
          {getText(CommonButtonText.CHECK_PRICE)}
        </ThemedButton>
      </VehicleContainer>
      {foundVehicleWithMissingConfigId && (
        <Select
          name="modelType"
          id="modelType"
          label={getText("carform.label.modeltype")}
          options={modelTypeOptions(
            VEHICLE_TYPE.CAR,
            findVehicleTextsCar.modelTypePlaceholder ?? "",
            models,
            formProps,
          )}
          onChange={(event) =>
            handleVehicleModelTypeChange(
              event,
              models.current,
              formProps as FormikProps<CarProductIntroFormValues>,
            )
          }
          error={getError("modelType", formProps)}
          disabled={
            !isDefined(vehicle) && (!modelsFetched || !!formProps.errors.model)
          }
          isLoading={models.isLoading}
          large
        />
      )}
      <ThemedButton
        as="button"
        type="button"
        variant="text"
        id={resetSearchTrackerId}
        onClick={() => dispatch(resetFindVehicle())}
        data-trackid={resetSearchTrackerId}
        trackId={resetSearchTrackerId}
      >
        {getText(CommonButtonText.SEARCH_AGAIN)}
      </ThemedButton>
    </>
  );
};
